import { Loader2, Plus, Search } from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import AssignWinnersModal from '../../components/admin/AssignWinnersModal'
import PrizeFormModal from '../../components/admin/PrizeFormModal'
import PrizeList from '../../components/admin/PrizeList'
import Layout from '../../components/Layout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import TypeAheadSearch from '../../components/ui/type-ahead-search'
import { useNotification } from '../../contexts/NotificationContext'
import { useUser } from '../../contexts/UserContext'
import {
  deletePrize as deletePrizeApi,
  fetchPrizes as fetchPrizesApi,
  fetchTournaments,
  searchGroupsForFilter,
} from '../../services/prizeService'

export default function PrizeManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const { user, loading: userLoading } = useUser()
  const notify = useNotification()
  const [prizes, setPrizes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterGroup, setFilterGroup] = useState(null)
  const [filterGroupLabel, setFilterGroupLabel] = useState('All Groups')
  const [filterTournament, setFilterTournament] = useState(null)
  const [filterTournamentLabel, setFilterTournamentLabel] = useState('All Tournaments')
  const [filterHasWinner, setFilterHasWinner] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingPrize, setEditingPrize] = useState(null)
  const [assigningWinner, setAssigningWinner] = useState(null)

  const searchTimeoutRef = useRef(null)
  const isAdmin = user?.role === 'admin'

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Search functions for type-ahead filters
  const searchGroups = async (query) => {
    try {
      // Add "Global Prizes" as first option
      const globalOption = { id: 'global', name: 'Global Prizes' }

      const data = await searchGroupsForFilter(query)

      // Prepend global option to results
      return [globalOption, ...(data || [])]
    } catch (error) {
      console.error('Error searching groups:', error)
      return [{ id: 'global', name: 'Global Prizes' }]
    }
  }

  const searchTournamentsFilter = async (query) => {
    try {
      const data = await fetchTournaments({
        p_search_term: query || null,
      })
      if (data) {
        return data.map(t => ({ id: t.id, name: t.name })).slice(0, 10)
      }
      return []
    } catch (error) {
      console.error('Error searching tournaments:', error)
      return []
    }
  }

  // Fetch prizes
  const fetchPrizes = useCallback(async () => {
    if (userLoading || !user) {
      return
    }
    if (!isAdmin) {
      notify.error('Admin access required')
      return
    }

    try {
      setLoading(true)
      const data = await fetchPrizesApi({
        p_search_term: debouncedSearchTerm || null,
        p_tournament_id: filterTournament || null,
        p_group_id: filterGroup === 'global' ? null : (filterGroup || null),
        p_has_winner: filterHasWinner === 'all' ? null : filterHasWinner === 'yes'
      })

      if (!data.success) throw new Error(data.error)

      setPrizes(data.prizes || [])
    } catch (err) {
      console.error('Error fetching prizes:', err)
      notify.error(err.message || 'Failed to fetch prizes')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearchTerm, filterTournament, filterGroup, filterHasWinner, isAdmin, notify, user, userLoading])

  useEffect(() => {
    fetchPrizes()
  }, [fetchPrizes])

  const handleDelete = async (prizeId) => {
    const confirmed = await notify.confirm({
      title: 'Delete Prize',
      description: 'Are you sure you want to delete this prize? This cannot be undone.',
      variant: 'destructive',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
    if (!confirmed) return

    try {
      await deletePrizeApi({
        p_prize_id: prizeId
      })
      notify.success('Prize deleted!')
      fetchPrizes()
    } catch (err) {
      console.error('Error deleting prize:', err)
      notify.error(err.message || 'Failed to delete prize')
    }
  }

  if (userLoading) {
    return (
      <Wrapper>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      </Wrapper>
    )
  }

  if (!isAdmin) {
    return (
      <Wrapper>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">
                Admin access required to view this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Prize Management</h1>
            <p className="text-muted-foreground mt-1">Manage prizes for tournaments and groups</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Create Prize
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search prizes..."
                  className="pl-9"
                />
              </div>

              <div>
                <TypeAheadSearch
                  value={filterGroup}
                  onChange={(group) => {
                    if (group === null) {
                      setFilterGroup(null)
                      setFilterGroupLabel('All Groups')
                    } else if (group.id === 'global') {
                      setFilterGroup('global')
                      setFilterGroupLabel('Global Prizes')
                    } else {
                      setFilterGroup(group.id)
                      setFilterGroupLabel(group.name)
                    }
                  }}
                  onSearch={searchGroups}
                  getItemLabel={(g) => g.name}
                  getItemValue={(g) => g.id}
                  placeholder={filterGroupLabel}
                  emptyMessage="No groups found"
                />
              </div>

              <div>
                <TypeAheadSearch
                  value={filterTournament}
                  onChange={(tournament) => {
                    if (tournament) {
                      setFilterTournament(tournament.id)
                      setFilterTournamentLabel(tournament.name)
                    } else {
                      setFilterTournament(null)
                      setFilterTournamentLabel('All Tournaments')
                    }
                  }}
                  onSearch={searchTournamentsFilter}
                  getItemLabel={(t) => t.name}
                  getItemValue={(t) => t.id}
                  placeholder={filterTournamentLabel}
                  emptyMessage="No tournaments found"
                />
              </div>

              <Select
                value={filterHasWinner}
                onValueChange={setFilterHasWinner}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Prizes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Prizes</SelectItem>
                  <SelectItem value="yes">Has Winner</SelectItem>
                  <SelectItem value="no">No Winner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Prizes List */}
        <PrizeList
          prizes={prizes}
          loading={loading}
          onEdit={setEditingPrize}
          onDelete={handleDelete}
          onAssignWinner={setAssigningWinner}
        />

        {/* Modals */}
        {showCreateModal && (
          <PrizeFormModal
            onClose={() => setShowCreateModal(false)}
            onSave={() => {
              fetchPrizes()
              setShowCreateModal(false)
            }}
          />
        )}
        {editingPrize && (
          <PrizeFormModal
            prize={editingPrize}
            onClose={() => setEditingPrize(null)}
            onSave={() => {
              fetchPrizes()
              setEditingPrize(null)
            }}
          />
        )}
        {assigningWinner && (
          <AssignWinnersModal
            prize={assigningWinner}
            onClose={() => setAssigningWinner(null)}
            onSave={() => {
              fetchPrizes()
              setAssigningWinner(null)
            }}
          />
        )}
      </div>
    </Wrapper>
  )
}
